// Iter 73 — Vendor Quotations (Phase 2 step 1)
// Tab on Vendor Detail listing this vendor's quotations + Add modal.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Plus, ArrowRight, Receipt, Trash, FileText,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    QUOTATION_STATUS_BADGE, QUOTATION_DEFAULT_ENUMS,
    formatAmount, formatMarginPct,
} from "./vendor_constants";

const BLANK_LINE = () => ({
    vendor_service_id: "",
    description: "",
    quantity: 1,
    unit_price: 0,
    gst_rate: 0,
    hsn_sac_code: "",
    notes: "",
});

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

const BLANK_QUOTE = () => ({
    quotation_ref: "",
    quotation_date: todayISO(),
    valid_until: addDaysISO(30),
    currency: "INR",
    notes: "",
    status: "draft",
    discount_amount: 0,
    tax_amount: 0,
    lines: [BLANK_LINE()],
});

export default function VendorQuotationsTab({ vendor }) {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get(`/vendors/${vendor.id}/quotations`),
            api.get(`/vendors/${vendor.id}/services`),
        ])
            .then(([qRes, sRes]) => {
                if (cancelled) return;
                setQuotes(qRes.data.items || []);
                setServices(sRes.data.items || []);
            })
            .catch((err) => {
                if (!cancelled) toast.error(formatApiError(err, "Failed to load quotations"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [vendor.id, reloadKey]);

    const activeServices = useMemo(
        () => services.filter((s) => s.status !== "cancelled"),
        [services],
    );

    if (loading) {
        return <div className="text-sm text-slate-500 py-8 text-center" data-testid="vendor-quotations-loading">Loading quotations…</div>;
    }

    return (
        <div data-testid="vendor-quotations-tab">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-medium text-slate-900">Quotations</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Record vendor quotes for one or more services. Once accepted, financials become immutable and the quote is ready for PO conversion.
                    </p>
                </div>
                {can("vendor:write") && (
                    <Button
                        data-testid="vendor-quotation-add-btn"
                        size="sm"
                        onClick={() => setDialogOpen(true)}
                        disabled={activeServices.length === 0}
                        title={activeServices.length === 0 ? "Add at least one active service first" : ""}
                        className="gap-1"
                    >
                        <Plus size={14} /> Add Quotation
                    </Button>
                )}
            </div>

            {activeServices.length === 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="vendor-quotations-need-services">
                    Add at least one vendor service before recording a quotation — quote lines must reference a service.
                </div>
            )}

            {quotes.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg" data-testid="vendor-quotations-empty">
                    <Receipt size={28} className="text-slate-300 mx-auto mb-2" />
                    <div className="text-sm text-slate-500">No quotations recorded</div>
                    {can("vendor:write") && activeServices.length > 0 && (
                        <div className="text-xs text-slate-400 mt-1">Click &quot;Add Quotation&quot; to record the first one.</div>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="vendor-quotations-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Ref</th>
                                <th className="text-left font-medium px-4 py-3">Date</th>
                                <th className="text-left font-medium px-4 py-3">Valid Until</th>
                                <th className="text-right font-medium px-4 py-3">Grand Total</th>
                                <th className="text-right font-medium px-4 py-3">Margin %</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {quotes.map((q) => {
                                const badge = QUOTATION_STATUS_BADGE[q.status] || QUOTATION_STATUS_BADGE.draft;
                                return (
                                    <tr
                                        key={q.id}
                                        data-testid={`vendor-quotation-row-${q.code}`}
                                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                        onClick={() => navigate(`/vendor-quotations/${q.id}`)}
                                    >
                                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{q.code}</td>
                                        <td className="px-4 py-3 text-slate-700 text-xs">{q.quotation_ref || "—"}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">{formatDate(q.quotation_date)}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">{formatDate(q.valid_until)}</td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-900">{formatAmount(q.grand_total, q.currency)}</td>
                                        <td className="px-4 py-3 text-right text-xs">
                                            {q.mapped_line_count > 0 ? (
                                                <span className={q.margin_pct >= 0 ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                                                    {formatMarginPct(q.margin_pct)}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge data-testid={`vendor-quotation-status-${q.code}`} className={`${badge.className} hover:${badge.className}`}>
                                                {badge.label}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <ArrowRight size={14} className="text-slate-400" />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <QuotationDialog
                key={dialogOpen ? "open" : "closed"}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={reload}
                vendor={vendor}
                services={activeServices}
            />
        </div>
    );
}

function QuotationDialog({ open, onClose, onSaved, vendor, services }) {
    const [form, setForm] = useState(() => ({
        ...BLANK_QUOTE(),
        currency: vendor.currency || "INR",
    }));
    const [busy, setBusy] = useState(false);

    const setField = (k) => (e) => {
        const v = e?.target?.type === "number" ? Number(e.target.value || 0) : e?.target?.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const setLine = (idx, key, value) => {
        setForm((f) => {
            const next = [...f.lines];
            next[idx] = { ...next[idx], [key]: value };
            // If switching service, auto-fill description from service name
            if (key === "vendor_service_id") {
                const svc = services.find((s) => s.id === value);
                if (svc && !next[idx].description) {
                    next[idx].description = svc.name;
                }
                // Also pre-fill unit_price from service amount as a sane starting point
                if (svc && (!next[idx].unit_price || next[idx].unit_price === 0)) {
                    next[idx].unit_price = Number(svc.amount) || 0;
                }
            }
            return { ...f, lines: next };
        });
    };

    const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, BLANK_LINE()] }));
    const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

    const totals = useMemo(() => {
        const subtotal = form.lines.reduce(
            (acc, ln) => acc + Number(ln.quantity || 0) * Number(ln.unit_price || 0), 0,
        );
        const lineGst = form.lines.reduce(
            (acc, ln) => acc + (Number(ln.quantity || 0) * Number(ln.unit_price || 0)) * Number(ln.gst_rate || 0) / 100, 0,
        );
        const grand = subtotal + lineGst - Number(form.discount_amount || 0) + Number(form.tax_amount || 0);
        return { subtotal, lineGst, grand };
    }, [form]);

    const submit = async () => {
        if (!form.quotation_date || !form.valid_until) { toast.error("Quotation date and valid-until are required"); return; }
        if (form.valid_until < form.quotation_date) { toast.error("Valid until must be on or after quotation date"); return; }
        if (form.lines.length === 0) { toast.error("Add at least one line item"); return; }
        for (const [i, ln] of form.lines.entries()) {
            if (!ln.vendor_service_id) { toast.error(`Line ${i + 1}: select a vendor service`); return; }
            if (Number(ln.quantity || 0) <= 0) { toast.error(`Line ${i + 1}: quantity must be > 0`); return; }
        }
        setBusy(true);
        try {
            const r = await api.post(`/vendors/${vendor.id}/quotations`, form);
            toast.success(`Quotation ${r.data.code} created`);
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl" data-testid="vendor-quotation-dialog">
                <DialogHeader>
                    <DialogTitle>Add Quotation</DialogTitle>
                    <DialogDescription>
                        For vendor {vendor.name} ({vendor.code}). Lines must reference an active service of this vendor.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                    {/* Header fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Vendor Reference">
                            <Input data-testid="quote-ref" value={form.quotation_ref} onChange={setField("quotation_ref")} placeholder="e.g. VQ-2026-001" />
                        </Field>
                        <Field label="Quotation Date *">
                            <Input data-testid="quote-date" type="date" value={form.quotation_date} onChange={setField("quotation_date")} />
                        </Field>
                        <Field label="Valid Until *">
                            <Input data-testid="quote-valid-until" type="date" value={form.valid_until} onChange={setField("valid_until")} />
                        </Field>
                        <Field label="Currency">
                            <Sel value={form.currency} onChange={setField("currency")} testId="quote-currency"
                                options={QUOTATION_DEFAULT_ENUMS.currencies.map((c) => [c, c])} />
                        </Field>
                        <Field label="Status">
                            <Sel value={form.status} onChange={setField("status")} testId="quote-status"
                                options={[["draft", "Draft"], ["received", "Received"]]} />
                        </Field>
                        <Field label="Document Discount">
                            <Input data-testid="quote-discount" type="number" min={0} value={form.discount_amount} onChange={setField("discount_amount")} />
                        </Field>
                        <Field label="Document Tax (additional)" hint="On top of per-line GST">
                            <Input data-testid="quote-tax" type="number" min={0} value={form.tax_amount} onChange={setField("tax_amount")} />
                        </Field>
                    </div>

                    {/* Lines */}
                    <div className="rounded-md border border-slate-200">
                        <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                            <div className="font-medium">Line Items</div>
                            <Button data-testid="quote-add-line" type="button" size="sm" variant="outline" onClick={addLine} className="gap-1 h-7">
                                <Plus size={12} /> Add Line
                            </Button>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {form.lines.map((ln, idx) => (
                                <div key={idx} className="p-3" data-testid={`quote-line-${idx}`}>
                                    <div className="grid grid-cols-12 gap-2">
                                        <div className="col-span-12 sm:col-span-5">
                                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Service *</Label>
                                            <select
                                                data-testid={`quote-line-${idx}-service`}
                                                value={ln.vendor_service_id}
                                                onChange={(e) => setLine(idx, "vendor_service_id", e.target.value)}
                                                className="mt-1 flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm"
                                            >
                                                <option value="">Select a service…</option>
                                                {services.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {`${s.code} · ${s.name}${s.catalogue_item_code ? ` · ${s.catalogue_item_code}` : ""}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Qty *</Label>
                                            <Input data-testid={`quote-line-${idx}-qty`} type="number" min={0} step="0.01"
                                                value={ln.quantity} onChange={(e) => setLine(idx, "quantity", Number(e.target.value || 0))} />
                                        </div>
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Unit Price *</Label>
                                            <Input data-testid={`quote-line-${idx}-price`} type="number" min={0} step="0.01"
                                                value={ln.unit_price} onChange={(e) => setLine(idx, "unit_price", Number(e.target.value || 0))} />
                                        </div>
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">GST %</Label>
                                            <Input data-testid={`quote-line-${idx}-gst`} type="number" min={0} max={30} step="0.01"
                                                value={ln.gst_rate} onChange={(e) => setLine(idx, "gst_rate", Number(e.target.value || 0))} />
                                        </div>
                                        <div className="col-span-6 sm:col-span-1 flex items-end justify-end">
                                            {form.lines.length > 1 && (
                                                <Button data-testid={`quote-line-${idx}-remove`} type="button" size="sm" variant="ghost"
                                                    onClick={() => removeLine(idx)}
                                                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-9 w-9 p-0">
                                                    <Trash size={14} />
                                                </Button>
                                            )}
                                        </div>
                                        <div className="col-span-12 sm:col-span-9">
                                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Description</Label>
                                            <Input data-testid={`quote-line-${idx}-desc`} value={ln.description}
                                                onChange={(e) => setLine(idx, "description", e.target.value)} />
                                        </div>
                                        <div className="col-span-12 sm:col-span-3">
                                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">HSN/SAC</Label>
                                            <Input data-testid={`quote-line-${idx}-hsn`} value={ln.hsn_sac_code}
                                                onChange={(e) => setLine(idx, "hsn_sac_code", e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Totals preview */}
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3" data-testid="quote-totals-preview">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <Stat label="Subtotal" value={formatAmount(totals.subtotal, form.currency)} />
                            <Stat label="Per-line GST" value={formatAmount(totals.lineGst, form.currency)} />
                            <Stat label="Doc tax / -discount" value={formatAmount(Number(form.tax_amount || 0) - Number(form.discount_amount || 0), form.currency)} />
                            <Stat label="Grand Total" value={formatAmount(totals.grand, form.currency)} highlight />
                        </div>
                    </div>

                    <Field label="Notes">
                        <Textarea data-testid="quote-notes" rows={2} value={form.notes} onChange={setField("notes")} />
                    </Field>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="quote-save-btn" onClick={submit} disabled={busy}>
                        {busy ? "Saving…" : "Create Quotation"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <Label className="text-xs text-slate-600 mb-1.5 inline-block">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
        </div>
    );
}

function Sel({ value, onChange, options, testId }) {
    return (
        <select
            data-testid={testId}
            value={value}
            onChange={onChange}
            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm"
        >
            {options.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
        </select>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
            <div className={`mt-0.5 font-mono ${highlight ? "text-slate-900 font-semibold text-sm" : "text-slate-700"}`}>
                {value}
            </div>
        </div>
    );
}
